import type { CompanyDashboardSummary } from '@/types/company.types'
import { Panel } from '@/components/ui'
import { cn } from '@/lib/cn'

export interface TodayPanelProps {
  summary?: CompanyDashboardSummary
  isLoading: boolean
}

interface Segment {
  key: string
  label: string
  count: number
  bar: string
  swatch: string
}

/**
 * Where every member stands right now, as one proportional bar.
 *
 * Four separate stat cards made the reader hold four numbers in their head to
 * work out coverage. One bar shows the ratio directly, and the counts read as a
 * legend under it.
 *
 * Every member is in exactly one segment (the BE counts distinct people at
 * their best status). "Not scheduled" is neutral — no shift is not an absence;
 * "Due later" is scheduled but not yet past start + grace.
 */
export function TodayPanel({ summary, isLoading }: TodayPanelProps): React.ReactElement {
  const present = summary?.presentToday ?? 0
  const late = summary?.lateToday ?? 0
  const halfDay = summary?.halfDayToday ?? 0
  const onLeave = summary?.onLeaveToday ?? 0
  const absent = summary?.absentToday ?? 0
  const notScheduled = summary?.notScheduledToday ?? 0
  const dueLater = summary?.dueLaterToday ?? 0
  const total = present + late + halfDay + onLeave + absent + notScheduled + dueLater

  const segments: Segment[] = [
    {
      key: 'present',
      label: 'Present',
      count: present,
      bar: 'bg-teal-600 dark:bg-teal-500',
      swatch: 'bg-teal-600 dark:bg-teal-500',
    },
    {
      key: 'late',
      label: 'Late',
      count: late,
      bar: 'bg-amber-500 dark:bg-amber-400',
      swatch: 'bg-amber-500 dark:bg-amber-400',
    },
    {
      key: 'halfDay',
      label: 'Half-day',
      count: halfDay,
      bar: 'bg-teal-400/70 dark:bg-teal-300/70',
      swatch: 'bg-teal-400/70 dark:bg-teal-300/70',
    },
    {
      key: 'leave',
      label: 'On leave',
      count: onLeave,
      bar: 'bg-sky-500/70 dark:bg-sky-400/70',
      swatch: 'bg-sky-500/70 dark:bg-sky-400/70',
    },
    {
      key: 'absent',
      label: 'Absent',
      count: absent,
      bar: 'bg-rose-500 dark:bg-rose-400',
      swatch: 'bg-rose-500 dark:bg-rose-400',
    },
    {
      key: 'dueLater',
      label: 'Due later',
      count: dueLater,
      bar: 'bg-gray-300 dark:bg-gray-500',
      swatch: 'bg-gray-300 dark:bg-gray-500',
    },
    {
      key: 'notScheduled',
      label: 'Not scheduled',
      count: notScheduled,
      bar: 'bg-gray-200 dark:bg-gray-600',
      swatch: 'bg-gray-200 dark:bg-gray-600',
    },
  ]

  const summaryText =
    total === 0
      ? 'No members are assigned to this company yet.'
      : segments
          .filter((s) => s.count > 0)
          .map((s) => `${s.count} ${s.label.toLowerCase()}`)
          .join(', ')

  return (
    <Panel
      as="section"
      aria-labelledby="today-heading"
      title={
        <h3
          id="today-heading"
          className="text-sm font-semibold tracking-[-0.006em] text-gray-900 dark:text-gray-100"
        >
          Today
        </h3>
      }
      action={
        <span className="text-xs font-medium tabular-nums text-gray-500 dark:text-gray-400">
          {isLoading ? '—' : `${summary?.clockedInNow ?? 0} clocked in now`}
        </span>
      }
    >
      <p className="sr-only">{isLoading ? 'Loading today’s attendance.' : summaryText}</p>

      {!isLoading && (summary?.memberCount ?? 0) === 0 ? (
        <p className="py-1 text-sm text-gray-500 dark:text-gray-400">
          No members are assigned to this company yet. Add members to start tracking attendance.
        </p>
      ) : (
        <>
      <div
        className={cn(
          'flex h-2 w-full gap-0.5 overflow-hidden rounded-full',
          (isLoading || total === 0) && 'bg-gray-100 dark:bg-gray-700'
        )}
        aria-hidden
      >
        {!isLoading &&
          total > 0 &&
          segments
            .filter((s) => s.count > 0)
            .map((s) => (
              <span
                key={s.key}
                className={cn('h-full first:rounded-l-full last:rounded-r-full', s.bar)}
                style={{ width: `${(s.count / total) * 100}%` }}
              />
            ))}
      </div>

      <dl className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
        {segments.map((s) => (
          <div key={s.key} className="flex items-center gap-1.5">
            <span className={cn('h-2 w-2 shrink-0 rounded-full', s.swatch)} aria-hidden />
            <dt className="text-xs text-gray-500 dark:text-gray-400">{s.label}</dt>
            <dd
              className={cn(
                'text-sm font-semibold tabular-nums',
                // A zero is context, not a finding — keep it recessive so the
                // non-zero figures are what the eye lands on.
                !isLoading && s.count === 0
                  ? 'text-gray-300 dark:text-gray-600'
                  : 'text-gray-900 dark:text-gray-100'
              )}
            >
              {isLoading ? '—' : s.count}
            </dd>
          </div>
        ))}
      </dl>
        </>
      )}
    </Panel>
  )
}

TodayPanel.displayName = 'TodayPanel'
